// ═══════════════════════════════════════════════════════════════
// TrashModal.tsx – Lomtár (szervezetenként)
//
// A törölt oldalak (a teljes tartalmukkal), bejegyzések / alpontok és mellékletek 30 napig
// visszaállíthatók. Visszaállítás: aki törölhet; végleges törlés és ürítés: admin.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from 'react';
import { ApiClient, type TrashItem } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { X, Trash2, RotateCcw, RefreshCw, FileText, Paperclip, LayoutList, Search, AlertTriangle } from 'lucide-react';

const KIND: Record<TrashItem['kind'], { label: string; Icon: React.FC<{ className?: string }> }> = {
  page: { label: 'Oldal', Icon: LayoutList },
  record: { label: 'Bejegyzés', Icon: FileText },
  attachment: { label: 'Melléklet', Icon: Paperclip },
};

const fmtDate = (s: string) => { const d = new Date(s.replace(' ', 'T') + 'Z'); return isNaN(+d) ? s : d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }); };
const daysLeft = (s: string) => Math.max(0, Math.ceil((+new Date(s.replace(' ', 'T') + 'Z') - Date.now()) / 86400000));
const fmtSize = (b: number) => (b <= 0 ? '' : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

export const TrashModal: React.FC<{ isOpen: boolean; onClose: () => void; onRestored?: (r: { page_id?: number; record_id?: number }) => void }> = ({ isOpen, onClose, onRestored }) => {
  const { role } = useAuth();
  const isAdmin = ['superadmin', 'tenant_admin', 'org_admin'].includes(String(role));
  const [items, setItems] = useState<TrashItem[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [kind, setKind] = useState<'all' | TrashItem['kind']>('all');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    const r = await ApiClient.getTrash();
    if (r.ok) { setItems(r.data || []); if (r.days) setDays(r.days); } else setError(r.error || 'A lomtár nem tölthető be');
    setLoading(false);
  };
  useEffect(() => { if (isOpen) { setNotice(null); load(); } }, [isOpen]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => (kind === 'all' || it.kind === kind) && (!s || `${it.title} ${it.page_title || ''}`.toLowerCase().includes(s)));
  }, [items, kind, q]);
  const counts = useMemo(() => ({ all: items.length, page: items.filter((i) => i.kind === 'page').length, record: items.filter((i) => i.kind === 'record').length, attachment: items.filter((i) => i.kind === 'attachment').length }), [items]);

  if (!isOpen) return null;

  const restore = async (it: TrashItem) => {
    setBusy(it.id); setError(null); setNotice(null);
    const r = await ApiClient.restoreTrashItem(it.id);
    if (r.ok) {
      setNotice(`Visszaállítva: ${KIND[it.kind].label.toLowerCase()} – „${it.title}”${r.renamed ? ` (az oldal azonosítója foglalt volt, új azonosító: ${r.renamed})` : ''}.`);
      setItems((xs) => xs.filter((x) => x.id !== it.id));
      onRestored?.(r);
      if (it.kind === 'page') load();
    } else setError(r.error || 'A visszaállítás nem sikerült');
    setBusy(null);
  };
  const purge = async (it: TrashItem) => {
    if (!window.confirm(`Véglegesen törlöd: „${it.title}”? Ez nem vonható vissza.`)) return;
    setBusy(it.id); setError(null);
    const r = await ApiClient.purgeTrashItem(it.id);
    if (r.ok) setItems((xs) => xs.filter((x) => x.id !== it.id)); else setError(r.error || 'A végleges törlés nem sikerült');
    setBusy(null);
  };
  const emptyAll = async () => {
    if (!window.confirm(`Kiüríted a lomtárat (${items.length} tétel)? A tételek és a fájlok véglegesen törlődnek, ez nem vonható vissza.`)) return;
    setBusy('all'); setError(null);
    const r = await ApiClient.emptyTrash();
    if (r.ok) { setItems([]); setNotice(`A lomtár kiürítve (${r.purged ?? 0} tétel).`); } else setError(r.error || 'Az ürítés nem sikerült');
    setBusy(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="trash-title"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      onClick={(e) => { if (e.target === e.currentTarget && busy === null) onClose(); }}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-4xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h2 id="trash-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-600" /> Lomtár</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              A törölt oldalak (teljes tartalmukkal), bejegyzések és mellékletek {days} napig visszaállíthatók, utána véglegesen törlődnek. A lomtárban lévő fájlok a tárhelykvótába beleszámítanak.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Típus">
            {(['all', 'page', 'record', 'attachment'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${kind === k ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs' : 'text-slate-500'}`}>
                {k === 'all' ? 'Összes' : KIND[k].label} <span className="ml-0.5 text-[10px] opacity-70">{counts[k]}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[10rem]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés…" aria-label="Keresés a lomtárban"
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white" />
          </div>
          <button type="button" onClick={load} title="Frissítés" className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && items.length > 0 && (
            <button type="button" onClick={emptyAll} disabled={busy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5" /> Lomtár ürítése
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-2">
          {error && <p role="alert" className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{error}</p>}
          {notice && <p role="status" className="text-xs text-emerald-700 dark:text-emerald-400">{notice}</p>}
          {!loading && !visible.length && <p className="py-12 text-center text-sm text-slate-500">{items.length ? 'Nincs találat.' : 'A lomtár üres.'}</p>}
          {visible.map((it) => {
            const { Icon, label } = KIND[it.kind];
            let detail: { records?: number; attachments?: number } = {};
            try { detail = it.detail ? JSON.parse(it.detail) : {}; } catch { detail = {}; }
            const left = daysLeft(it.expires_at);
            return (
              <div key={it.id} className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 dark:text-white break-words">{it.title || '(cím nélkül)'}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-2">
                    <span className="font-semibold">{label}</span>
                    {it.kind === 'page' && <span>{detail.records ?? 0} bejegyzés · {detail.attachments ?? 0} melléklet</span>}
                    {it.kind !== 'page' && it.page_title && <span>Oldal: {it.page_title}</span>}
                    {fmtSize(it.size_bytes) && <span>{fmtSize(it.size_bytes)}</span>}
                    <span>Törölve: {fmtDate(it.deleted_at)}{it.deleted_by_name ? ` · ${it.deleted_by_name}` : ''}</span>
                    <span className={left <= 3 ? 'text-rose-600 dark:text-rose-400 font-semibold' : ''}>még {left} napig</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => restore(it)} disabled={busy !== null}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer disabled:opacity-40">
                    {busy === it.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Visszaállítás
                  </button>
                  {isAdmin && (
                    <button type="button" onClick={() => purge(it)} disabled={busy !== null} aria-label={`${it.title} végleges törlése`} title="Végleges törlés"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer disabled:opacity-40">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrashModal;
